import { useLayoutEffect, useRef, useState, type ComponentType } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { IconeSetaBaixo } from './icones'
import { MenuMaisPaginas } from './MenuMaisPaginas'
import { CLASSE_DO_ITEM_ATIVO, CLASSE_DO_ITEM_INATIVO } from './navegacao-estilo'
import { juntar } from './ui'

export interface ItemDeNavegacao {
  para: string
  rotulo: string
  Icone: ComponentType<{ className?: string }>
  /** Ativo só no endereço exato: o Calendário do Marketing é `/marketing`, raiz das outras. */
  exato?: boolean
}

/** O `gap-1` entre os itens, em pixels, para a conta de quantos cabem. */
const ESPACO_ENTRE_ITENS = 4

const CLASSE_DO_ITEM = 'relative flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-sm'

/**
 * A fila de telas do topo, com o "Mais" colado no fim.
 *
 * Em janela estreita, as telas que não cabem saem da fila, da última para a
 * primeira, e entram no topo do "Mais". Antes a fila rolava de lado: numa
 * janela de meia tela, "Logs" ficava cortado atrás de uma barra de rolagem
 * que ninguém percebe que existe. Assim nenhuma tela some; só muda de lugar.
 *
 * A conta usa a largura de verdade de cada item, medida numa cópia invisível
 * da fila: rótulos têm tamanhos diferentes, e uma largura média esconderia
 * "Estatísticas" cedo demais ou deixaria "Calendário" passar da borda.
 */
export function NavegacaoPrincipal({ itens }: { itens: ItemDeNavegacao[] }) {
  const local = useLocation()
  const [quantosCabem, setQuantosCabem] = useState(itens.length)
  /*
    O "Mais" só com o ícone da tela aberta.

    No celular nenhuma tela cabe na fila, e o "Mais" leva o nome da aberta ("Bate-papo",
    "Calendário"): com 90px sobrando entre a marca e o sino, ele passava por cima do sino. Quando o
    nome não cabe, fica o ícone; o nome continua no `title` e no leitor de tela.
  */
  const [maisCompacto, setMaisCompacto] = useState(false)
  const faixa = useRef<HTMLDivElement>(null)
  const medidor = useRef<HTMLDivElement>(null)
  const mais = useRef<HTMLDivElement>(null)

  const ativo = (item: ItemDeNavegacao) =>
    item.exato ? local.pathname === item.para : local.pathname.startsWith(item.para)
  // O `Layout` monta a lista de novo a cada render; a chave só muda quando
  // muda de fato quais telas a pessoa pode ver.
  const chaveDosItens = itens.map((item) => item.para).join('|')

  useLayoutEffect(() => {
    const elementoFaixa = faixa.current
    const elementoMedidor = medidor.current
    if (!elementoFaixa || !elementoMedidor) return

    const calcular = () => {
      const disponivel = elementoFaixa.clientWidth
      const larguras = Array.from(elementoMedidor.children).map(
        (filho) => filho.getBoundingClientRect().width,
      )
      // O último filho do medidor é o botão "Mais" com o rótulo curto.
      const larguraMaisCurto = larguras.pop() ?? 0

      const somaDe = (quantidade: number) =>
        larguras.slice(0, quantidade).reduce((soma, largura) => soma + largura, 0) +
        Math.max(quantidade - 1, 0) * ESPACO_ENTRE_ITENS

      // O "Mais" só aparece se alguém sair da fila.
      if (somaDe(larguras.length) <= disponivel) {
        setQuantosCabem(larguras.length)
        setMaisCompacto(false)
        return
      }

      /*
        O "Mais" mostra o nome da tela aberta quando ela está dentro dele, e
        fica mais largo. A reserva usa a largura que ele tem agora, e nunca
        menos que a do rótulo curto. Não entra em vaivém: se esconder a tela
        aberta alarga o "Mais", sobra ainda menos espaço, e ela continua
        escondida.
      */
      const reservaDoMais =
        Math.max(mais.current?.offsetWidth ?? 0, larguraMaisCurto) + ESPACO_ENTRE_ITENS

      let cabem = 0
      while (cabem < larguras.length && somaDe(cabem + 1) + reservaDoMais <= disponivel) cabem++
      setQuantosCabem(cabem)

      /*
        A tela aberta dentro do "Mais" ocupa a largura dela na fila mais a seta (6px de espaço e
        14px de ícone). Sem esse espaço, compacto. Não entra em vaivém: compacto, o "Mais" nunca
        fica mais estreito que o rótulo curto, que já é o mínimo da reserva acima.
      */
      const indiceDoAtual = itens.findIndex(ativo)
      const sobraParaOMais = disponivel - somaDe(cabem) - (cabem > 0 ? ESPACO_ENTRE_ITENS : 0)
      setMaisCompacto(
        indiceDoAtual >= cabem && (larguras[indiceDoAtual] ?? 0) + 20 > sobraParaOMais,
      )
    }

    calcular()
    const observador = new ResizeObserver(calcular)
    observador.observe(elementoFaixa)
    observador.observe(elementoMedidor)
    if (mais.current) observador.observe(mais.current)
    return () => observador.disconnect()
  }, [chaveDosItens, local.pathname])

  const naFila = itens.slice(0, quantosCabem)
  const noMais = itens.slice(quantosCabem)

  return (
    <div ref={faixa} className="relative flex min-w-0 flex-1 items-center gap-1">
      <nav className="flex min-w-0 items-center gap-1">
        {naFila.map((item) => (
          <NavLink
            key={item.para}
            to={item.para}
            end={item.exato}
            className={({ isActive }) =>
              juntar(
                CLASSE_DO_ITEM,
                'transition',
                isActive ? CLASSE_DO_ITEM_ATIVO : CLASSE_DO_ITEM_INATIVO,
              )
            }
          >
            <item.Icone className="h-4 w-4 shrink-0" />
            {item.rotulo}
          </NavLink>
        ))}
      </nav>

      <div ref={mais} className="shrink-0">
        <MenuMaisPaginas extras={noMais} compacto={maisCompacto} />
      </div>

      {/*
        A cópia invisível que dá as larguras. Fica fora do fluxo e fora da
        leitura de tela, com todos os itens sempre presentes: medir os itens
        de verdade não daria a largura dos que já foram para o "Mais".
      */}
      <div
        ref={medidor}
        aria-hidden="true"
        className="pointer-events-none invisible absolute left-0 top-0 flex w-max gap-1"
      >
        {itens.map((item) => (
          <span
            key={item.para}
            className={juntar(CLASSE_DO_ITEM, ativo(item) && 'font-semibold')}
          >
            <item.Icone className="h-4 w-4 shrink-0" />
            {item.rotulo}
          </span>
        ))}
        <span className={CLASSE_DO_ITEM}>
          Mais
          <IconeSetaBaixo className="h-3.5 w-3.5 shrink-0" />
        </span>
      </div>
    </div>
  )
}
